import React from 'react';
import PropTypes from 'prop-types';

import FontClass from '@components/asset/shared/fontClass';
import BrightcovePlayer from '@components/video/brightcove_player/main';
import sanitize from '@helpers/sanitize';
import CustomFieldsAccordion from './customFieldsAccordion';
import PersonDetails from './personDetails';
import AssetTags from './assetTags';
import PDFViewer from './pdfViewer';
import VideoJSPlayer from '../../../../video/videojs_player/main';

/* NOTE : Anytime you do work in this component make sure there is parody within
the orgSearchResultsAssetDetails component. At some point these should become one
component. Right now they are separated because this component is getting the majority
of it's information from rails, whereas the orgSearchResultsAssetDetails is
getting it's information from an ajax call, which at this point is not sufficient
to receive the information and do all the actions we need */

class AssetDetails extends React.Component {
  get brandfolder() {
    return BF.fx.brandfolder();
  }

  get collection() {
    return BF.fx.collection();
  }

  get assetBackgroundColor() {
    const backgroundColor = this.props.backgroundColor;
    return backgroundColor && backgroundColor !== "default"
      ? { background: backgroundColor }
      : { backgroundColor: "transparent" };
  }

  renderAssetSpecificDescription() {
    const { asset, assetData } = this.props;

    switch (asset.type) {
      case "Person":
        return (
          <PersonDetails
            email={assetData.email}
            linkedin={asset.linkedin_url}
            name={asset.name}
            phoneNumber={assetData.phone_number}
            title={assetData.title}
            twitter={asset.twitter}
            twitterUrl={asset.twitter_url}
          />
        );
      case "Press":
        return (
          <div className="m-show-description">
            {asset.url ? (
              <a href={asset.url} target="blank">
                {" "}
                {asset.url}
              </a>
            ) : (
              ""
            )}
            {asset.published_date ? <div> {asset.published_date} </div> : ""}
          </div>
        );
      default:
        break;
    }
    return undefined;
  }

  renderHeader() {
    let videoParts;
    let videoType;
    let videoURL;
    let videoID;
    let videoOptions;
    let playbackID;
    let accountID;
    const { asset, elementDisplay, threeSixtyViewer } = this.props;

    if (elementDisplay) {
      if (asset.type === "Color") {
        return (
          <div
            className="s-color-bg-show"
            style={{ backgroundColor: elementDisplay }}
          />
        );
      }

      if (elementDisplay.startsWith("brightcove|")) {
        videoParts = elementDisplay.split("|");
        accountID = videoParts[1];
        videoID = videoParts[2];

        if (videoID && videoID.length) {
          return (
            <BrightcovePlayer
              attrs={{
                id: "advanced-video",
                className: "m-show-full-size-display"
              }}
              accountID={accountID}
              videoID={videoID}
              playerID="default"
            />
          );
        }

        // Special placeholder video and player
        return (
          <BrightcovePlayer
            attrs={{
              id: "advanced-video",
              className: "m-show-full-size-display processing"
            }}
            accountID={accountID}
            videoID="6045002940001"
            playerID="OV7Vi4enS"
          />
        );
      }

      if (elementDisplay.startsWith("mux|")) {
        videoParts = elementDisplay.split("|");
        playbackID = videoParts[1];
        videoURL = videoParts[2];

        if (playbackID && playbackID.length) {
          videoOptions = {
            sources: [
              {
                src: videoURL,
                type: "application/x-mpegURL"
              }
            ]
          };

          return (
            <div
              id="advanced-video"
              className="m-show-full-size-display s-expand-view"
              controls
            >
              <VideoJSPlayer
                className="m-show-full-size-display"
                {...videoOptions}
              />
              ;
            </div>
          );
        }
      }

      if (elementDisplay.startsWith("video|")) {
        videoParts = elementDisplay.split("|");
        videoType = videoParts[1];
        videoURL = videoParts[2];
        videoOptions = {
          sources: [
            {
              src: videoURL,
              type: videoType
            }
          ]
        };

        return (
          <div
            id="advanced-video"
            className="m-show-full-size-display s-expand-view"
            controls
          >
            <VideoJSPlayer
              className="m-show-full-size-display"
              {...videoOptions}
            />
            ;
          </div>
        );
      }

      let previewHtml = elementDisplay;
      const hasATag = elementDisplay.indexOf("</a>") > 0;

      if (hasATag) {
        const firstTag = elementDisplay.split("</a>")[0];
        const newTagHtml = '<div class="view-asset-new-tab-link"><span>Open in new tab</span></div></a>';
        previewHtml = `${firstTag}${newTagHtml}`;
      }

      if (threeSixtyViewer) {
        return (
          <div className="m-show-full-size-display">
            <div id="three-sixty-viewer" />
          </div>
        );
      }

      if (elementDisplay.startsWith('pdf')) {
        const splitDisplay = elementDisplay.split('|');
        const url = splitDisplay[1];
        if (splitDisplay.length > 2 && splitDisplay[2].includes('view-only')) {
          return (
            <PDFViewer fileUrl={url} />
          );
        }

        return (
          <div className="pdf-viewer--with-tab-link">
            <PDFViewer fileUrl={url} />
            <a
              className="view-asset-new-tab-link"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Open in new tab')}
            </a>
          </div>
        );
      }

      const stretchClass = asset.type !== "ExternalMedium" && hasATag ? "stretch" : "";
      const videoClass = asset.type === "ExternalMedium" ? "external-medium-container" : "";

      return (
        <React.Fragment>
          <div
            className={`m-show-full-size-display ${stretchClass} ${videoClass}`}
            data-private
            style={this.assetBackgroundColor}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </React.Fragment>
      );
    }

    if (asset.type === "Font") {
      return (
        <div className="m-show-full-size-display">
          <FontClass asset={asset}>
            Aa Bb Cc Dd Ee Ff Gg Hh Jj Kk Ll Mm Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx
            Yy Zz 0123456789
          </FontClass>
        </div>
      );
    }

    return undefined;
  }

  render() {
    const {
      approvedBy,
      awaitingApproval,
      collectionExpiration,
      customFields,
      expired,
      expirationDate,
      formattedDescription,
      publishDate,
      tags,
      unpublished,
      updatedAndCreated
    } = this.props;

    return (
      <React.Fragment>
        {this.renderHeader()}
        {updatedAndCreated ? (
          <div
            dangerouslySetInnerHTML={{ __html: sanitize(updatedAndCreated) }}
          />
        ) : (
          ""
        )}
        {approvedBy ? (
          <div dangerouslySetInnerHTML={{ __html: approvedBy }} />
        ) : (
          ""
        )}
        {awaitingApproval ? (
          <div dangerouslySetInnerHTML={{ __html: awaitingApproval }} />
        ) : (
          ""
        )}
        {collectionExpiration ? (
          <div dangerouslySetInnerHTML={{ __html: collectionExpiration }} />
        ) : (
          ""
        )}

        {publishDate ? (
          <div className="s-modal-published-on">
            {`${
              unpublished ? t("Will Publish on") : t("Published on")
            } ${publishDate}`}
          </div>
        ) : (
          ""
        )}

        {expirationDate ? (
          <div className="s-modal-expires-on">
            {`${expired ? t("Expired on") : t("Expires on")} ${expirationDate}`}
          </div>
        ) : (
          ""
        )}

        {tags.length > 0 ? <AssetTags tags={tags} orgSearchPage={false} /> : ""}

        {this.renderAssetSpecificDescription()}

        {formattedDescription ? (
          <div className="m-show-description s-asset-description">
            <span className="s-asset-details-title modal-label">
              {t("Description")}
            </span>
            <p
              dangerouslySetInnerHTML={{
                __html: this.props.formattedDescription
              }}
            />
          </div>
        ) : (
          ""
        )}

        {customFields && Object.keys(customFields).length > 0 ? (
          <CustomFieldsAccordion count={6} customFields={customFields} />
        ) : (
          ""
        )}
      </React.Fragment>
    );
  }
}

AssetDetails.propTypes = {
  approvedBy: PropTypes.string,
  asset: PropTypes.shape({}).isRequired,
  assetData: PropTypes.shape({}),
  awaitingApproval: PropTypes.string,
  backgroundColor: PropTypes.string,
  collectionExpiration: PropTypes.string,
  customFields: PropTypes.shape({}).isRequired,
  elementDisplay: PropTypes.string,
  expired: PropTypes.bool.isRequired,
  expirationDate: PropTypes.string,
  formattedDescription: PropTypes.string,
  publishDate: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  threeSixtyViewer: PropTypes.bool,
  unpublished: PropTypes.bool.isRequired,
  updatedAndCreated: PropTypes.string.isRequired
};

AssetDetails.defaultProps = {
  approvedBy: "",
  assetData: {},
  awaitingApproval: "",
  backgroundColor: "",
  collectionExpiration: "",
  elementDisplay: "",
  expirationDate: "",
  formattedDescription: "",
  publishDate: "",
  threeSixtyViewer: false
};

export default AssetDetails;
